/**
 * DropVideoStep
 *
 * First step of the add content flow.
 * Displays a drag-and-drop zone and browse button for video file selection.
 */

import React, { useCallback, useRef, useState } from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { ExpauseButton, ButtonVariant } from '@/presentation/components/common/controls/ExpauseButton';
import { ExpauseLoadingIndicator } from '@/presentation/components/common/loadingIndicators/ExpauseLoadingIndicator';
import { useRegisterStepNav } from '@/presentation/components/common/stepper';
import { remoteConfigService } from '@/domain/services/remoteConfig/remoteConfigService';
import { MathUtils } from '@/domain/utils/mathUtils';
import { ANIMATION_CONSTANTS } from '@/presentation/constants/animationConstants';
import { SUPPORTED_VIDEO_ACCEPT } from '@/domain/utils/videoFormatUtils';
import { qaAttr } from '@/presentation/utils/qaAttr';

interface DropVideoStepProps {
  validationError: string | null;
  isProcessing: boolean;
  onFileDrop: (file: File) => void;
  onCancel: () => void;
  /**
   * Accepted file types for the hidden file input. Defaults to videos only
   * (user flow); the chat flow passes videos + images.
   */
  accept?: string;
  /**
   * Drop-zone heading copy. Defaults to the user-flow video-only string
   * (`dropVideoHere`); the chat flow overrides it with the image-or-video
   * copy (`dropMediaHere`).
   */
  title?: string;
  /**
   * Drop-zone description copy. Defaults to the user-flow video-duration
   * string; the chat flow overrides it with the max-only chat copy.
   */
  description?: string;
}

export const DropVideoStep: React.FC<DropVideoStepProps> = ({
  validationError,
  isProcessing,
  onFileDrop,
  onCancel,
  accept,
  title,
  description,
}) => {
  const { theme } = useTheme();
  const { l10n } = useLocale();
  const responsive = useResponsiveDesign();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      onFileDrop(file);
    }
  }, [onFileDrop]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onFileDrop(file);
    }
    // Reset so the same file can be re-selected
    e.target.value = '';
  }, [onFileDrop]);

  const handleBrowseClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  useRegisterStepNav(
    {
      hideNext: true,
      showPrevious: true,
      previousLabel: l10n.common.cancel,
      previousIcon: null,
      onPrevious: onCancel,
    },
    [l10n.common.cancel, onCancel],
  );

  const minSeconds = remoteConfigService.contentMinSeconds;
  const maxMinutes = MathUtils.secondsToMinutesCeil(remoteConfigService.contentMaxSeconds);

  const descriptionText = description ?? l10n.publish.dropVideoDescription
    .replace('{min}', String(minSeconds))
    .replace('{max}', String(maxMinutes));

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        padding: responsive.spacing.xl,
        gap: responsive.spacing.lg,
      }}
    >
      {/* Drop zone */}
      <div
        {...qaAttr({ id: 'add-content-dropzone', status: isProcessing ? 'selected' : 'empty' })}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height: responsive.dropZone.height,
          padding: responsive.dropZone.padding,
          borderRadius: responsive.dropZone.borderRadius,
          backgroundColor: isDragOver ? `${theme.exclusive}10` : `${theme.secondary}`,
          transition:
            `background-color ${ANIMATION_CONSTANTS.duration.normal}ms`,
          cursor: 'pointer',
        }}
        onClick={handleBrowseClick}
      >
        {isProcessing ? (
          <ExpauseLoadingIndicator />
        ) : (
          <>
            <p
              style={{
                color: theme.text.primary,
                fontSize: responsive.font.base,
                fontWeight: responsive.fontWeight.semibold,
                textAlign: 'center',
                marginBottom: responsive.spacing.sm,
              }}
            >
              {title ?? l10n.publish.dropVideoHere}
            </p>
            {validationError ? (
              <p
                style={{
                  color: theme.danger,
                  fontSize: responsive.font.sm,
                  textAlign: 'center',
                }}
              >
                {validationError}
              </p>
            ) : (
              <p
                style={{
                  color: theme.text.secondary,
                  fontSize: responsive.font.sm,
                  textAlign: 'center',
                }}
              >
                {descriptionText}
              </p>
            )}
          </>
        )}
      </div>

      {/* Hidden file input — carries the injectable `add-content-pick-file`
          qa-id so QA can bypass the native file chooser (assign a File via a
          DataTransfer + dispatch a bubbling `change`). */}
      <input
        {...qaAttr({ id: 'add-content-pick-file' })}
        ref={fileInputRef}
        type="file"
        accept={accept ?? SUPPORTED_VIDEO_ACCEPT}
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />

      {/* Browse button */}
      <ExpauseButton
        {...qaAttr({ id: 'add-content-browse' })}
        variant={ButtonVariant.secondary}
        onClick={handleBrowseClick}
        disabled={isProcessing}
      >
        {l10n.publish.browseFiles}
      </ExpauseButton>
    </div>
  );
};
